'use client';

import { type TheBoatDBConfigType, useConfig } from '@marine/configs';
import styles from '@styles/ApiKeys/ApiKeys.module.scss';
import { Button } from 'antd';
import Link from 'next/link';

/**
 * What someone without Pro sees in place of a create form.
 *
 * Shown whenever the API says `canCreateKeys` is false — the same entitlement
 * check that decides `POST /v1/keys`, so this and the button it replaces can
 * never disagree. It is the *explanation* half of that: hiding "Create key"
 * without saying why leaves a page that looks broken rather than gated.
 *
 * The two shapes it takes are the two situations it covers. Someone who has
 * never subscribed is being told what the API is; someone whose subscription
 * lapsed already knows, has keys in the table below, and needs to hear that
 * re-subscribing restores them rather than a pitch.
 */

type Props = {
  /** Whether this account already holds keys, i.e. it lapsed rather than never subscribed. */
  hasExistingKeys: boolean;
};

const UpgradePrompt: React.FC<Props> = ({ hasExistingKeys }) => {
  const { manageSubscriptionUrl } = useConfig<TheBoatDBConfigType>();

  return (
    <div className={`${styles.panel} ${styles.upgrade}`}>
      <h2 className={styles.upgrade__title}>
        {hasExistingKeys ? 'Creating keys needs an active Pro subscription' : 'API keys are part of Pro'}
      </h2>
      <p className={styles.upgrade__text}>
        {hasExistingKeys
          ? 'Your Pro subscription is not active, so no new keys can be issued. Your existing keys are listed below and can still be revoked.'
          : 'The public API gives you programmatic access to TheBoatDB’s catalogue of boats and manufacturers. Creating an API key requires a Pro subscription — everything else on this page is available to you already.'}
      </p>

      {/* The figures are the ones the API actually enforces; see the rate limits
          page, which is generated from the same plan configuration. */}
      <ul className={styles.upgrade__list}>
        <li>10,000 requests a month, shared across every key on your subscription</li>
        <li>60 requests a minute, per key</li>
        <li>Up to 10 active keys — one per integration, each revocable on its own</li>
        <li>Read access to both the boats and manufacturers endpoints</li>
      </ul>

      <div className={styles.upgrade__actions}>
        {manageSubscriptionUrl !== undefined && (
          <Button type="primary" variant="solid" style={{ color: 'white' }} href={manageSubscriptionUrl}>
            {hasExistingKeys ? 'Restore subscription' : 'Upgrade to Pro'}
          </Button>
        )}
        <Button variant="outlined" href="/pricing">
          Compare plans
        </Button>
      </div>

      <p className={styles.footnote}>
        Not sure yet? The <Link href="/docs/api">API documentation</Link> and the{' '}
        <Link href="/docs/api/rate-limits">rate limits</Link> are readable without a subscription.
      </p>
    </div>
  );
};

export { UpgradePrompt };
